import React, { useState, useEffect } from 'react';

/**
 * Fetches and displays a KB document (markdown or JSON) in a modal.
 * Use this instead of opening /api/kb/xxx in a new tab so content always shows.
 */
function DocViewerModal({ isOpen, url, title, onClose }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !url) {
      setContent('');
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Document not found' : `Failed to load (${res.status})`);
        return res.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Could not load document');
        setContent('');
        setLoading(false);
      });
  }, [isOpen, url]);

  if (!isOpen) return null;

  let body = null;
  if (loading) body = <div className="doc-viewer__loading">Loading…</div>;
  else if (error) body = <div className="doc-viewer__error">{error}</div>;
  else if (content) {
    const isJson = url && url.toLowerCase().endsWith('.json');
    const displayContent = isJson
      ? (() => {
          try {
            return JSON.stringify(JSON.parse(content), null, 2);
          } catch {
            return content;
          }
        })()
      : content;
    body = (
      <pre className="doc-viewer__content">
        {displayContent}
      </pre>
    );
  }

  return (
    <div className="doc-viewer-overlay" role="dialog" aria-modal="true" aria-label={title || 'Document viewer'}>
      <div className="doc-viewer-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="doc-viewer-modal card">
        <div className="doc-viewer__header">
          <h3 className="doc-viewer__title">{title || 'Document'}</h3>
          <button type="button" className="doc-viewer__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="doc-viewer__body">
          {body}
        </div>
      </div>
    </div>
  );
}

export default DocViewerModal;
